import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Store, Link2, Unlink, RefreshCw, Package, Check, X, 
  Loader2, AlertCircle, ExternalLink, Settings, CheckCircle2,
  AlertTriangle, Info, ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { supermarket } from '@/lib/api';

const MarketplaceIntegrationTab = ({ onImportComplete }) => {
  const { t } = useTranslation();
  
  const [integrations, setIntegrations] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [fetchingProducts, setFetchingProducts] = useState(false);
  
  // Connect dialog state
  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [credentials, setCredentials] = useState({});
  
  // Fetched products state
  const [fetchedProducts, setFetchedProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [integrationsRes, connectionsRes, categoriesRes] = await Promise.all([
        supermarket.getMarketplaceIntegrations(),
        supermarket.getMarketplaceConnections(),
        supermarket.getImportCategories()
      ]);
      
      setIntegrations(integrationsRes.data.integrations || []);
      setConnections(connectionsRes.data.connections || []);
      setCategories(categoriesRes.data.categories || []);
    } catch (error) {
      console.error('Failed to load marketplace data:', error);
      toast.error(t('sellerPanel.marketplace.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    if (!selectedPlatform) return;
    
    // Validate required fields
    const platform = integrations.find(i => i.id === selectedPlatform);
    const requiredFields = platform?.required_fields || [];
    
    for (const field of requiredFields) {
      if (!credentials[field.key]) {
        toast.error(`${field.label} gerekli`);
        return;
      }
    }
    
    setConnecting(true);
    try {
      const response = await supermarket.connectMarketplace(selectedPlatform, credentials);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setShowConnectDialog(false);
        setCredentials({});
        setSelectedPlatform(null);
        loadData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('sellerPanel.marketplace.connectError'));
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async (platform) => {
    if (!window.confirm(t('sellerPanel.marketplace.disconnectConfirm'))) return;
    
    try {
      await supermarket.disconnectMarketplace(platform);
      toast.success(t('sellerPanel.marketplace.disconnected'));
      loadData();
      setFetchedProducts([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('sellerPanel.marketplace.disconnectError'));
    }
  };

  const handleFetchProducts = async (platform) => {
    setFetchingProducts(true);
    setFetchedProducts([]);
    
    try {
      const response = await supermarket.fetchMarketplaceProducts(platform);
      
      if (response.data.success) {
        setFetchedProducts(response.data.products || []);
        setSelectedProducts(new Set(response.data.products?.map((_, i) => i) || []));
        
        if (response.data.is_mock) {
          toast.info(response.data.message, { duration: 5000 });
        } else {
          toast.success(`${response.data.total} ürün çekildi`);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('sellerPanel.marketplace.fetchError'));
    } finally {
      setFetchingProducts(false);
    }
  };

  const toggleProduct = (index) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedProducts(newSelected);
  };

  const toggleAll = () => {
    if (selectedProducts.size === fetchedProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(fetchedProducts.map((_, i) => i)));
    }
  };

  const updateProductCategory = (index, categoryId) => {
    const updatedProducts = [...fetchedProducts];
    updatedProducts[index] = {
      ...updatedProducts[index],
      category: categoryId
    };
    setFetchedProducts(updatedProducts);
  };

  const handleSaveProducts = async () => {
    const productsToSave = fetchedProducts.filter((_, i) => selectedProducts.has(i));
    
    if (productsToSave.length === 0) {
      toast.error(t('sellerPanel.import.selectAtLeastOne'));
      return;
    }
    
    setSaving(true);
    try {
      const response = await supermarket.saveImportedProducts(productsToSave);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setFetchedProducts([]);
        setSelectedProducts(new Set());
        
        if (onImportComplete) {
          onImportComplete();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('sellerPanel.marketplace.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const getConnectionStatus = (platformId) => {
    return connections.find(c => c.platform === platformId);
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || categoryId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>{t('sellerPanel.marketplace.title')}</AlertTitle>
        <AlertDescription>
          {t('sellerPanel.marketplace.description')}
        </AlertDescription>
      </Alert>

      {/* Marketplace Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {integrations.map((integration) => {
          const connection = getConnectionStatus(integration.id);
          const isConnected = !!connection;
          const isComingSoon = integration.status === 'coming_soon';
          
          return (
            <Card 
              key={integration.id} 
              className={`relative ${isComingSoon ? 'opacity-60' : ''} ${isConnected ? 'border-green-500 bg-green-50/50' : ''}`}
            >
              {isComingSoon && (
                <Badge className="absolute top-2 right-2" variant="secondary">
                  {t('sellerPanel.marketplace.comingSoon')}
                </Badge>
              )}
              {isConnected && connection.is_mock && (
                <Badge className="absolute top-2 right-2 bg-yellow-500">
                  MOCK
                </Badge>
              )}
              
              <CardHeader>
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{integration.icon}</span>
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {integration.name}
                      {isConnected && <CheckCircle2 className="w-5 h-5 text-green-600" />}
                    </CardTitle>
                    <CardDescription>{integration.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-1">
                    {integration.features?.slice(0, 3).map((feature, i) => (
                      <Badge key={i} variant="outline" className="text-xs">
                        {feature}
                      </Badge>
                    ))}
                  </div>
                  
                  {isConnected && (
                    <div className="text-sm text-muted-foreground mt-2">
                      <p>{t('sellerPanel.marketplace.connectedAs')}: {connection.platform_info?.seller_name}</p>
                      {connection.last_sync && (
                        <p>{t('sellerPanel.marketplace.lastSync')}: {new Date(connection.last_sync).toLocaleString('tr-TR')}</p>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
              
              <CardFooter className="flex gap-2">
                {isConnected ? (
                  <>
                    <Button 
                      variant="default" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => handleFetchProducts(integration.id)}
                      disabled={fetchingProducts}
                    >
                      {fetchingProducts ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Package className="w-4 h-4 mr-1" />
                      )}
                      {t('sellerPanel.marketplace.fetchProducts')}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDisconnect(integration.id)}
                    >
                      <Unlink className="w-4 h-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      variant="default" 
                      size="sm" 
                      className="flex-1"
                      disabled={isComingSoon}
                      onClick={() => {
                        setSelectedPlatform(integration.id);
                        setCredentials({});
                        setShowConnectDialog(true);
                      }}
                    >
                      <Link2 className="w-4 h-4 mr-1" />
                      {t('sellerPanel.marketplace.connect')}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => window.open(integration.docs_url, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {/* Connect Dialog */}
      <Dialog open={showConnectDialog} onOpenChange={setShowConnectDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {integrations.find(i => i.id === selectedPlatform)?.icon}
              {integrations.find(i => i.id === selectedPlatform)?.name} {t('sellerPanel.marketplace.connection')}
            </DialogTitle>
            <DialogDescription>
              {t('sellerPanel.marketplace.enterCredentials')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {integrations.find(i => i.id === selectedPlatform)?.required_fields?.map((field) => (
              <div key={field.key}>
                <Label>{field.label}</Label>
                <Input
                  type={field.type}
                  placeholder={field.placeholder}
                  value={credentials[field.key] || ''}
                  onChange={(e) => setCredentials({...credentials, [field.key]: e.target.value})}
                />
              </div>
            ))}
            
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                {t('sellerPanel.marketplace.mockNote')}
              </AlertDescription>
            </Alert>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConnectDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleConnect} disabled={connecting}>
              {connecting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Link2 className="w-4 h-4 mr-2" />
              )}
              {t('sellerPanel.marketplace.connect')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fetched Products */}
      {fetchedProducts.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  {t('sellerPanel.marketplace.fetchedProducts')} ({fetchedProducts.length})
                </CardTitle>
                <CardDescription>
                  {t('sellerPanel.marketplace.selectToImport')}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={toggleAll}>
                  {selectedProducts.size === fetchedProducts.length 
                    ? t('sellerPanel.import.deselectAll') 
                    : t('sellerPanel.import.selectAll')}
                </Button>
                <Badge variant="secondary">
                  {selectedProducts.size} {t('sellerPanel.import.selected')}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-3">
                {fetchedProducts.map((product, index) => (
                  <div 
                    key={index}
                    className={`p-4 border rounded-lg transition-all ${
                      selectedProducts.has(index) ? 'border-primary bg-primary/5' : ''
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <Checkbox
                        checked={selectedProducts.has(index)}
                        onCheckedChange={() => toggleProduct(index)}
                      />
                      
                      {product.image_url && (
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          className="w-16 h-16 object-cover rounded"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium truncate">{product.name}</h4>
                          <Badge variant="outline" className="text-xs">
                            {product.source_type}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground truncate">
                          {product.description?.slice(0, 80)}...
                        </p>
                        <div className="flex items-center gap-4 mt-2">
                          <span className="text-lg font-bold text-primary">
                            ₺{product.price?.toFixed(2)}
                          </span>
                          {product.compare_price && (
                            <span className="text-sm text-muted-foreground line-through">
                              ₺{product.compare_price?.toFixed(2)}
                            </span>
                          )}
                          {product.stock > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {t('sellerPanel.import.stock')}: {product.stock}
                            </Badge>
                          )}
                          {product.brand && (
                            <Badge variant="outline" className="text-xs">
                              {product.brand}
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="min-w-[180px]">
                        <Select
                          value={product.category || product.suggested_category?.category_id || ''}
                          onValueChange={(value) => updateProductCategory(index, value)}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue placeholder={t('sellerPanel.import.selectCategory')} />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id}>
                                {cat.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        {product.suggested_category && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            AI: {getCategoryName(product.suggested_category.category_id)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
            
            <div className="flex justify-end gap-4 mt-6 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => {
                  setFetchedProducts([]);
                  setSelectedProducts(new Set());
                }}
              >
                <X className="w-4 h-4 mr-2" />
                {t('sellerPanel.import.cancel')}
              </Button>
              <Button 
                onClick={handleSaveProducts}
                disabled={selectedProducts.size === 0 || saving}
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                {selectedProducts.size} {t('sellerPanel.import.saveProducts')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MarketplaceIntegrationTab;
